import React, { useState } from 'react';
import api from '../api';
import PropTypes from 'prop-types';
import './PlayoffPredictionForm.css';

const PlayoffPredictionForm = ({ playoff, onSuccess, onCancel }) => {
  const [homeScore, setHomeScore] = useState('');
  const [awayScore, setAwayScore] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Check if user already has a prediction
  const userPrediction = playoff.predictions?.find(p => p.user_id === playoff.current_user_id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    const home = parseInt(homeScore);
    const away = parseInt(awayScore);

    if (isNaN(home) || isNaN(away)) {
      setError('Please enter valid scores (non-negative integers)');
      return;
    }

    if (home < 0 || away < 0) {
      setError('Scores must be non-negative integers');
      return;
    }

    if (!window.confirm(`Submit prediction: ${playoff.home_team_name || 'Home'} ${home}, ${playoff.away_team_name || 'Away'} ${away}?\n\nThis cannot be changed after submission.`)) {
      return;
    }

    try {
      setSubmitting(true);
      await api.submitPlayoffPrediction(playoff.id, home, away);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Error submitting prediction:', err);
      setError(err.response?.data?.message || 'Failed to submit prediction. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // If user already submitted, show their prediction
  if (userPrediction) {
    return (
      <div className="playoff-prediction-form">
        <div className="prediction-submitted">
          <h3>✅ Your Prediction Submitted</h3>
          <div className="prediction-display">
            <div className="score-display">
              <div className="team-score">
                <span className="team-name">{playoff.home_team_name || 'Home'}</span>
                <span className="score">{userPrediction.home_team_score}</span>
              </div>
              <div className="vs">vs</div>
              <div className="team-score">
                <span className="team-name">{playoff.away_team_name || 'Away'}</span>
                <span className="score">{userPrediction.away_team_score}</span>
              </div>
            </div>
            <p className="submitted-time">
              Submitted: {new Date(userPrediction.submitted_at).toLocaleString()}
            </p>
            <p className="note">Predictions cannot be changed after submission.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="playoff-prediction-form">
      <h3>Make Your Prediction</h3>
      {playoff.game_id && (
        <div className="game-info">
          <p><strong>Game:</strong> {playoff.home_team_name} vs {playoff.away_team_name}</p>
          {playoff.prediction_deadline && (
            <p><strong>Deadline:</strong> {new Date(playoff.prediction_deadline).toLocaleString()}</p>
          )}
        </div>
      )}
      
      {error && (
        <div className="error-message">
          ❌ {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="prediction-form">
        <div className="score-inputs">
          <div className="score-input-group">
            <label htmlFor="home-score">
              {playoff.home_team_name || 'Home Team'} Score
            </label>
            <input
              type="number"
              id="home-score"
              min="0"
              value={homeScore}
              onChange={(e) => setHomeScore(e.target.value)}
              required
              disabled={submitting}
              placeholder="0"
            />
          </div>

          <div className="vs-divider">vs</div>

          <div className="score-input-group">
            <label htmlFor="away-score">
              {playoff.away_team_name || 'Away Team'} Score
            </label>
            <input
              type="number"
              id="away-score"
              min="0"
              value={awayScore}
              onChange={(e) => setAwayScore(e.target.value)}
              required
              disabled={submitting}
              placeholder="0"
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="submit"
            className="submit-button"
            disabled={submitting || !homeScore || !awayScore}
          >
            {submitting ? 'Submitting...' : 'Submit Prediction'}
          </button>
          {onCancel && (
            <button
              type="button"
              className="cancel-button"
              onClick={onCancel}
              disabled={submitting}
            >
              Cancel
            </button>
          )}
        </div>

        <p className="form-note">
          ⚠️ Once submitted, your prediction cannot be changed.
        </p>
      </form>
    </div>
  );
};

PlayoffPredictionForm.propTypes = {
  playoff: PropTypes.object.isRequired,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func
};

export default PlayoffPredictionForm;

